import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/lib/seo";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";

export default function Signup() {
  const [form, setForm] = useState({ email: "", password: "", display_name: "" });
  const [busy, setBusy] = useState(false);
  const { signup } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await signup(form.email, form.password, form.display_name);
      toast.success("Welcome to Asylum");
      nav("/pricing");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Seo title="Sign Up — Start Distributing Your Music" path="/signup" description="Create your free Asylum Entertainment account, pick a plan from $9.99/year and release your music to Spotify, Apple Music and 150+ stores." />
      <Navbar />
      <div className="min-h-[calc(100vh-73px)] grid lg:grid-cols-2">
        <div className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-4">— Join</div>
            <h1 className="font-display text-4xl uppercase tracking-tighter mb-10">Sign Up.</h1>
            <label className="block mb-6">
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Artist / Display Name</div>
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} required data-testid="signup-name"
                className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-sm focus:border-red-600 focus:outline-none" />
            </label>
            <label className="block mb-6">
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Email</div>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" required data-testid="signup-email"
                className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-sm focus:border-red-600 focus:outline-none" />
            </label>
            <label className="block mb-8">
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Password (min 8 chars)</div>
              <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} type="password" minLength={8} required data-testid="signup-password"
                className="w-full bg-transparent border border-zinc-800 px-4 py-3 text-sm focus:border-red-600 focus:outline-none" />
            </label>
            <button type="submit" disabled={busy} data-testid="signup-submit"
              className="w-full bg-red-600 text-white py-4 text-sm tracking-[0.2em] uppercase btn-brutal btn-brutal-red disabled:opacity-50">
              {busy ? "Creating..." : "Create Account"}
            </button>
            <div className="mt-6 text-sm text-zinc-500">
              Already in? <Link to="/login" className="text-red-500 hover:underline" data-testid="signup-to-login">Sign in</Link>
            </div>
          </form>
        </div>
        <div className="hidden lg:block relative border-l border-zinc-800 bg-zinc-950 order-1 lg:order-2">
          <div className="absolute inset-0 flex flex-col justify-between p-12">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 text-right">— Est. 2026</div>
            <div>
              <h2 className="font-display text-5xl uppercase tracking-tighter leading-[0.95] mb-4">
                Sign up.<br />Start <span className="text-red-600">releasing.</span>
              </h2>
              <p className="text-zinc-500 text-sm">Free account. Pick a plan when you're ready to distribute.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
